import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import handleError from '@fuse/core/errorHandler';
import { Close } from '@mui/icons-material';
import { Button, IconButton, TextField, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCreateArticleMutation } from 'src/app/services/articlesApi';
import MarkdownEditor from '@uiw/react-markdown-editor';
import useMessage from 'src/app/hooks/useMessage';
import UploaderFile from './Components/NewUploader';

const NewsDialog = ({ post, onOpen, onClose, refetchList }) => {
  const { t } = useTranslation('translations');
  const [createArticle, { isLoading }] = useCreateArticleMutation();
  const { showMessage } = useMessage();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    mode: 'onChange',
    defaultValues: post || {
      active: true,
      titleUz: '',
      title: '',
      text: '',
      textUz: '',
      articleFileIds: [],
    },
  });

  const onSubmit = (data) => {
    if (!data.text || !data.textUz) {
      showMessage({
        message: t('FillNewsText'),
        autoHideDuration: 3000,
        variant: 'error',
      });
      return;
    }

    createArticle(data)
      .unwrap()
      .then((res) => {
        if (res) {
          handleClose();
          showMessage({
            message: t('NewsSuccessfullyAdded'),
            autoHideDuration: 3000,
            variant: 'success',
          });
          refetchList();
        }
      })
      .catch(handleError);
  };

  const handleClose = (_event, reason) => {
    if (reason === 'backdropClick') {
      return;
    } else {
      onClose();
    }
    reset();
  };

  return (
    <div>
      <Dialog
        open={onOpen}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="xl"
      >
        <DialogTitle id="alert-dialog-title" className="relative">
          <IconButton onClick={handleClose} className="absolute top-0 right-0">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <div className="flex flex-col md:flex-row">
            <form onSubmit={handleSubmit(onSubmit)}>
              <Typography component="p" className="mb-20" variant="h6">
                {t('NewPost')}
              </Typography>

              <div className="flex flex-col gap-20 mb-20 md:flex-row">
                <div>
                  <Controller
                    name="titleUz"
                    control={control}
                    rules={{ required: 'Sarlavha majburiy' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        size="small"
                        className="mb-10"
                        fullWidth
                        label={'Sarlavha (uzb.)'}
                        variant="outlined"
                        error={!!errors?.titleUz}
                        helperText={errors?.titleUz?.message}
                      />
                    )}
                  />

                  <Typography variant="subtitle1" className="mb-10">
                    Yangilik matni
                  </Typography>

                  <Controller
                    name="textUz"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <MarkdownEditor
                        {...field}
                        className="min-h-[400px] w-full md:max-w-[400px]"
                      />
                    )}
                  />
                </div>
                <div>
                  <Controller
                    name="title"
                    control={control}
                    rules={{ required: 'Заголовок обязателен' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        size="small"
                        className="mb-10"
                        fullWidth
                        label={'Заголовок (rus.)'}
                        variant="outlined"
                        error={!!errors?.title}
                        helperText={errors?.title?.message}
                      />
                    )}
                  />
                  <Typography variant="subtitle1" className="mb-10">
                    Текст новости
                  </Typography>
                  <Controller
                    name="text"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <MarkdownEditor
                        {...field}
                        className="min-h-[400px] w-full  md:max-w-[400px] "
                      />
                    )}
                  />
                </div>
              </div>
              <Controller
                name="isPonarama"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <div className="flex items-center gap-10 mb-10">
                    <input
                      type="checkbox"
                      id="isPonarama"
                      {...field}
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      disabled={!!watch('articleFileIds')?.length}
                    />
                    <label htmlFor="isPanorama">{t('Panorama')}</label>
                  </div>
                )}
              />
              <div className="w-full">
                <UploaderFile
                  accept="image/*"
                  name="articleFileIds"
                  setValue={setValue}
                  isPanorama={watch('isPonarama')}
                />
              </div>
              <div className="flex items-stretch justify-end gap-10">
                <Button variant="contained" className="dark:bg-lightblack" onClick={handleClose}>
                  {t('Cancel')}
                </Button>
                <Button
                  variant="contained"
                  className="dark:text-white"
                  color="success"
                  type="submit"
                  disabled={isLoading}
                >
                  {t('Publish')}
                </Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NewsDialog;
